import React, { useState, useEffect } from 'react';
import { Users, AlertTriangle, TrendingUp, Clock } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface HeatmapData {
  x: number;
  y: number;
  value: number;
  location: string;
}

interface Alert {
  location: string;
  type: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  timestamp: string;
}

interface Prediction {
  time: string;
  location: string;
  expectedOccupancy: number;
  confidence: number;
}

const OccupantHeatmap: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([
    {
      location: 'Main Lobby',
      type: 'Congestion',
      severity: 'high',
      message: 'High traffic detected',
      timestamp: '09:45 AM'
    },
    {
      location: 'Cafeteria',
      type: 'Capacity',
      severity: 'medium',
      message: 'Approaching capacity',
      timestamp: '12:30 PM'
    },
    {
      location: 'Conference Zone',
      type: 'Flow',
      severity: 'low',
      message: 'Unusual movement pattern',
      timestamp: '02:15 PM'
    }
  ]);

  const [predictions, setPredictions] = useState<Prediction[]>([
    {
      time: '10:00 AM',
      location: 'Main Lobby',
      expectedOccupancy: 85,
      confidence: 92
    },
    {
      time: '12:45 PM',
      location: 'Cafeteria',
      expectedOccupancy: 95,
      confidence: 88
    },
    {
      time: '03:00 PM',
      location: 'Conference Zone',
      expectedOccupancy: 65,
      confidence: 85
    }
  ]);

  // Generate mock heatmap data
  useEffect(() => {
    const generateHeatmapData = () => {
      const newData: HeatmapData[] = [];
      const locations = ['Main Lobby', 'Cafeteria', 'Conference Zone', 'Open Office', 'Meeting Rooms'];
      
      // Generate 50 data points
      for (let i = 0; i < 50; i++) {
        const location = locations[Math.floor(Math.random() * locations.length)];
        newData.push({
          x: Math.floor(Math.random() * 100),
          y: Math.floor(Math.random() * 100),
          value: Math.floor(Math.random() * 100),
          location
        });
      }
      
      setHeatmapData(newData);
    };

    generateHeatmapData();
    const interval = setInterval(generateHeatmapData, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Real-Time Occupant Heatmap</h2>
      
      {/* Heatmap Visualization */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Live Occupancy Heatmap</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid />
              <XAxis type="number" dataKey="x" name="x" unit="" />
              <YAxis type="number" dataKey="y" name="y" unit="" />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }}
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const data = payload[0].payload as HeatmapData;
                    return (
                      <div className="bg-white shadow rounded p-2">
                        <p className="font-semibold">{data.location}</p>
                        <p>Occupancy: {data.value}%</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Scatter
                data={heatmapData}
                fill="#8884d8"
                fillOpacity={0.6}
                shape={(props: any) => {
                  const { cx, cy, fill } = props;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={10}
                      fill={fill}
                      fillOpacity={0.6}
                    />
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Real-time Alerts */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Real-time Alerts</h3>
          <AlertTriangle className="text-yellow-500" />
        </div>
        <div className="space-y-4">
          {alerts.map((alert, index) => (
            <div key={index} className="border-l-4 border-yellow-500 pl-4 py-2">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">{alert.location}</h4>
                  <p className="text-sm text-gray-600">{alert.message}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(alert.severity)}`}>
                    {alert.type}
                  </span>
                  <span className="text-sm text-gray-500">{alert.timestamp}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Predictive Analytics */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Predictive Analytics</h3>
          <TrendingUp className="text-blue-500" />
        </div>
        <div className="space-y-4">
          {predictions.map((prediction, index) => (
            <div key={index} className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium">{prediction.location}</h4>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-1" />
                    {prediction.time}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-blue-800">{prediction.expectedOccupancy}%</div>
                  <div className="text-sm text-blue-600">
                    {prediction.confidence}% confidence
                  </div>
                </div>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2"
                  style={{ width: `${prediction.expectedOccupancy}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OccupantHeatmap; 